import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import RobotsMaintenanceRecordAddForm from './robots-maintenance-record-add-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMaintenanceRecordsByRobotId, fetchMaintenanceRecordsDetails } from './store/slice';
import { selectRobotMaintenanceRecordsData, selectMaintenanceRecordData } from './store/selectors';
import { format } from 'date-fns';

const initData = {
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  serviceRecords: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const formatDate = date => {
  if (!date) return '--';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch (error) {
    return '--';
  }
};

const RobotsMaintenanceHistory = ({ data, robotId, onCloseModal }) => {
  const dispatch = useDispatch();
  const [showMaintenanceHistory, setShowMaintenanceHistory] = useState(true);
  const [formData, setFormData] = useState(initData);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [maintenanceRecordId, setMaintenanceRecordId] = useState(null);

  const robotMaintenanceRecordsData = useSelector(selectRobotMaintenanceRecordsData);
  const maintenanceRecordData = useSelector(selectMaintenanceRecordData);

  useEffect(() => {
    if (robotId) {
      dispatch(fetchMaintenanceRecordsByRobotId(robotId));
    }
  }, [robotId]);

  useEffect(() => {
    if (maintenanceRecordData && !showMaintenanceHistory) {
      setFormData({
        ...initData,
        ...maintenanceRecordData,
        personnelRecords: maintenanceRecordData.personnelRecords || initData.personnelRecords,
      });
    }
  }, [maintenanceRecordData, showMaintenanceHistory]);

  const handleMaintenanceRecord = () => {
    if (showMaintenanceHistory) {
      // Going to add new maintenance record
      setFormData(initData);
      setMaintenanceRecordId(null);
      setShowMaintenanceHistory(false);
    } else {
      // Going back to maintenance history
      setShowMaintenanceHistory(true);
      setFormData(initData);
      setMaintenanceRecordId(null);
      // Refresh the maintenance records list
      if (robotId) {
        dispatch(fetchMaintenanceRecordsByRobotId(robotId));
      }
    }
  };

  const handleEditClick = record => {
    setViewingRecord(record);
    setMaintenanceRecordId(record.id);
    dispatch(fetchMaintenanceRecordsDetails(record.id));
    setShowMaintenanceHistory(false);
  };

  const columns = [
    {
      title: 'Work Order Number',
      dataIndex: 'workOrderNumber',
      key: 'workOrderNumber',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: date => formatDate(date),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: text => text || '--',
    },
    {
      title: 'Maintenance Type',
      dataIndex: 'maintenanceType',
      key: 'maintenanceType',
      render: type => {
        const types = { '1': 'Preventive', '2': 'Corrective', '3': 'Emergency' };
        return types[type] || '--';
      },
    },
    {
      title: 'Personnel Name',
      dataIndex: 'personnelName',
      key: 'personnelName',
      render: text => text || '--',
    },
    {
      title: 'Down Time',
      dataIndex: 'downTime',
      key: 'downTime',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <div>
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => handleEditClick(record)}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="maintenance-history-container">
      <div className="mb-3">
        <button className="btn btn-info me-2" onClick={() => handleMaintenanceRecord()}>
          {showMaintenanceHistory ? '+ Add Maintenance Record' : '← Back to Maintenance History'}
        </button>
      </div>

      {showMaintenanceHistory ? (
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <Table columns={columns} dataSource={robotMaintenanceRecordsData} />
          </div>
        </div>
      ) : (
        <RobotsMaintenanceRecordAddForm
          key={formData?.workOrderNumber || 'new'}
          data={formData}
          robotId={robotId}
          maintenanceRecordId={maintenanceRecordId}
          onCloseModal={onCloseModal}
        />
      )}
    </div>
  );
};

RobotsMaintenanceHistory.propTypes = {
  data: PropTypes.object,
  robotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseModal: PropTypes.func,
};

export default RobotsMaintenanceHistory;

